import { format, parseJSON } from 'date-fns';
import { produce } from 'immer';
import {
  type ChangeEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { Can, useAuth, useCan } from '@gbs-monorepo-packages/auth';
import {
  BaseDropdown,
  BaseTooltip,
  Button,
  DefaultDescriptionBenefitEducation,
  FeatureFlagKey,
  FormBase,
  FormModal,
  HighUserRoles,
  MainContainer,
  NotEmployeeRoles,
  PageHeader,
  Roles,
  SearchBar,
  SelectData,
  TRUNCATE_MAX,
  TemplatesType,
  formatUnknownErrorMessage,
  groupsTypes,
  truncateString,
  useFeatureFlag,
  useToast,
} from '@gbs-monorepo-packages/common';
import { SelectContainer } from '@gbs-monorepo/edge/components/SelectContainer';
import {
  CreateViewURLSchema,
  type DownloadCourseSchema,
  type DuplicateCourseSchema,
  type GenerateCourseTemplateByCourseSchema,
  type StatusCourseSchema,
  createViewURLSchema,
  downloadCourseSchema,
  duplicateCourseSchema,
  generateCourseTemplateByCourseSchema,
  statusCourseSchema,
} from '@gbs-monorepo/edge/formSchemas/courseSchema';
import { zodResolver } from '@hookform/resolvers/zod';

import LoadingSpinnerAnimated from '../../assets/spinner.svg';
import { CenteredText } from '../../components/CenteredText';
import { ContentPagination } from '../../components/ContentPagination';
import { UploadModal } from '../../components/UploadModal';
import { languageData } from '../../constants/Languages';
import { COURSES, EDIT_COURSE, REDIRECT } from '../../constants/RoutePaths';
import { type IEditCourseState } from '../../constants/RouteStates';
import { useClients } from '../../hooks/useCompanies';
import {
  type IApiThrowsError,
  type IPaginationMetaProps,
  adminApi,
} from '../../services/api';
import {
  type ICourseDTO,
  type ICourseVersion,
  type ICreateCourseFromTemplatesProps,
  type ICreateCourseUsingCourseGroupProps,
  type IPaginationCoursetDTO,
  changeImageCourse,
  createCourse,
  createCourseFromTemplates,
  createCourseUsingCourseGroup,
  deleteCourse,
  downloadCourse,
  duplicateCourse,
  getCoursesByClientId,
  getCoursesById,
  restoreCourseHistory,
  toggleBookmarkCourse,
  updateCourse,
} from '../../services/courses';
import {
  type IAutobuildGenerate,
  type IQuoteDateDTO,
  generateAutobuild,
  getQuotesDatesByCompany,
} from '../../services/quote';
import { createTemplateGroupByCourse } from '../../services/templates';
import { getRouteFrom } from '../../utils/getRoutes';
import Logger from '../../utils/logger';
import { AddCourseWithCoursesTemplateModal } from './components/AddCourseWithCourseTemplateModal';
import { AddCourseWithTemplateModal } from './components/AddCourseWithTemplateModal';
import { AutoBuildModal } from './components/AutoBuildModal';
import { CourseActions as CourseActionsComponent } from './components/CourseActions';
import { DuplicateCourseModal } from './components/DuplicateCourseModal';
import { RestorePageModal } from './components/RestoreCourseModal';
import {
  ArrowDown,
  ButtonContent,
  ConcludedIcon,
  ContentCard,
  CourseActionsContainer,
  CourseCard,
  CourseCardGrid,
  CourseName,
  Date,
  DisabledIcon,
  DropdownButtonContainer,
  DropdownItem,
  GridContent,
  GridIcon,
  GridItem,
  GridListOptions,
  IconContainer,
  ImageContainer,
  ImageContainerGrid,
  LazyImageCustom,
  LazyImageGrid,
  ListIcon,
  Loading,
  LoadingContainer,
  LoadingList,
  MainContainerCustom,
  ModalText,
  OnDesktopView,
  OnMobileView,
  Option,
  TextContent,
  ViewURLInputContainer,
  ViewURLModalContentWrapper,
} from './styles';

const NotViewer = NotEmployeeRoles;

const CourseNameWithTooltip = ({
  title,
  courseId,
  course,
  dataCy = `courses-${courseId}-name`,
}: {
  title: string;
  courseId: number;
  course: ICourseDTO;
  dataCy?: string;
}) => {
  const courseNameRef = useRef<HTMLHeadingElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (courseNameRef.current) {
        const element = courseNameRef.current;
        const truncated = element.scrollHeight > element.clientHeight;
        setIsTruncated(truncated);
      }
    };

    const animationFrameId = requestAnimationFrame(checkTruncation);

    return () => cancelAnimationFrame(animationFrameId);
  }, [title]);

  const courseNameElement = (
    <CourseName ref={courseNameRef} data-cy={dataCy}>
      {title}
      {course.status === 'Completed' && (
        <ConcludedIcon data-cy={course.status} />
      )}
      {course.status === 'Disabled' && <DisabledIcon data-cy={course.status} />}
    </CourseName>
  );

  const shouldShowTooltip = isTruncated || title.length > 80;

  return (
    <BaseTooltip
      message={title}
      dataCy="course-name-tooltip"
      disabled={!shouldShowTooltip}
    >
      {courseNameElement}
    </BaseTooltip>
  );
};

const templatesOptionsFiltered = groupsTypes.filter(
  (i) => i.key !== TemplatesType.BUILD_SCRATCH
);

const templateOptions = groupsTypes.filter(
  (i) =>
    i.key === TemplatesType.MY_TEMPLATES ||
    i.key === TemplatesType.SHARE_TEMPLATES
);

const RolesNotViewer = { roles: NotViewer };
const COURSES_PATH = getRouteFrom(COURSES);
const HEADER_IMAGE_MAX_SIZE = 10485760; // in bytes = 10 MB
const HEADER_IMAGE_MAX_WIDTH = 600;
const HEADER_IMAGE_MAX_HEIGHT = 400;

export const Courses = (): JSX.Element => {
  const isSelectOpen = useRef(new Set());
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [changeImage, setChangeImage] = useState<File | null>(null);
  const [courses, setCourses] = useState<ICourseDTO[]>([]);
  const { clientId = '' } = useParams(); // need to save this value to COMPANIES route
  const clientIdNumber = useMemo(() => Number(clientId), [clientId]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [courseToChangeImage, setCourseToChangeImage] =
    useState<ICourseDTO | null>(null);
  const [loadingChangeImage, setLoadingChangeImage] = useState(false);
  const isModalChangeImageOpen = !!courseToChangeImage;

  const [courseToDelete, setCourseToDelete] = useState<ICourseDTO | null>(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingCourseRestore, setLoadingCourseRestore] = useState(false);
  const isModalDeleteOpen = !!courseToDelete;
  const [courseToChangeStatus, setCourseToChangeStatus] =
    useState<ICourseDTO | null>(null);
  const [loadingChangeStatus, setLoadingChangeStatus] = useState(false);
  const isModalChangeStatusOpen = !!courseToChangeStatus;
  const [courseToCreateViewURL, setCourseToCreateViewURL] =
    useState<ICourseDTO | null>(null);
  const [loadingCreateViewURL, setLoadingCreateViewURL] = useState(false);
  const [isModalCreateViewURLOpen, setIsModalCreateViewURLOpen] =
    useState(false);
  const [
    isModalCreateCourseFromTemplateOpen,
    setIsModalCreateCourseFromTemplateOpen,
  ] = useState(false);
  const [
    isModalCreateCourseFromCourseTemplateOpen,
    setIsModalCreateCourseFromCourseTemplateOpen,
  ] = useState(false);
  const [isDropdownAddCourseOpen, setIsDropdownAddCourseOpen] = useState(false);
  const [isDropdownActionsOpen, setIsDropdownActionsOpen] = useState(false);

  const [courseToDownload, setCourseToDownload] = useState<ICourseDTO | null>(
    null
  );

  const [search, setSearch] = useState<string>('');
  const lastSearch = useRef(search);
  const lastClientId = useRef(clientId);
  const [isDropdownOpenGrid, setIsDropdownGridOpen] = useState(false);
  const [paginationMeta, setPaginationMeta] =
    useState<IPaginationMetaProps | null>(null);

  const isModalDownloadOpen = !!courseToDownload;
  const { addToast } = useToast();
  const { selectedClient, selectedListOptionStyle, selectListOptionStyle } =
    useClients();

  const { user, getCurrentRole } = useAuth();
  const currentRole = getCurrentRole(user?.roles ?? ['']);
  const [dialogDuplicateOpen, setDialogDuplicateOpen] = useState(false);
  const [loadingDuplicate, setLoadingDuplicate] = useState(false);
  const [optionStyle, setOptionStyle] = useState<string | null>(
    selectedListOptionStyle
  );
  const [courseDuplicate, setCourseDuplicate] = useState<ICourseDTO | null>(
    null
  );

  const [courseRestore, setCourseRestore] = useState<ICourseDTO | null>(null);
  const [courseGroupTemplate, setCourseGroupTemplate] =
    useState<ICourseDTO | null>(null);
  const [
    loadingGenerateCourseTemplateByCourse,
    setLoadingGenerateCourseTemplateByCourse,
  ] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [errorMessageUpload, setErrorMessageUpload] = useState('');
  const [visibleWhenCourseOptionsOpen, setVisibleWhenCourseOptionsOpen] =
    useState<number | null>(null);

  const [isClientIdReady, setIsClientIdReady] = useState(true);
  const [isModalAutoBuildOpen, setIsModalAutoBuildOpen] = useState(false);
  const [loadingQuoteAction, setLoadingQuoteAction] = useState<boolean>(false);
  const [quotesDatesSelected, setQuotesDatesSelected] = useState<
    IQuoteDateDTO | undefined
  >(undefined);

  const { isEnabled: isHiddenAutoBuild } = useFeatureFlag({
    api: adminApi,
    flagKey: FeatureFlagKey.HIDE_AUTO_BUILD,
  });

  const templateTypesOptions = useMemo(() => {
    if (!currentRole) {
      return templateOptions;
    }

    const isAdmin = currentRole.key === Roles.ADMIN;
    const isHighUser = HighUserRoles.includes(currentRole.key) && !isAdmin;

    if (isHighUser) {
      return templateOptions;
    }
    if (isAdmin) {
      return templatesOptionsFiltered;
    }
    return templateOptions;
  }, [currentRole]);

  const resetFormDownload = () => {
    if (!isSelectOpen.current.size) {
      setCourseToDownload(null);
      resetDownload({
        orientation: 'portrait',
        language: 'en',
      });
    }
  };

  useEffect(() => {
    if (localStorage.getItem('originalRoute')) {
      localStorage.removeItem('originalRoute');
    }
  }, []);

  useEffect(() => {
    if (!isClientIdReady) return;

    let mount = true;
    const syncCourses = async () => {
      try {
        setLoadingCourses(true);
        setCourses([]);
        setPaginationMeta(null);
        const result = await getCoursesByClientId({
          clientId,
          page: 1,
          limit: 15,
          filter: search,
        });

        if (mount) {
          setCourses(result.data);
          setPaginationMeta(result.meta);
          setIsClientIdReady(false);
        }
      } catch (err) {
        Logger.debug('err: ', err);
      } finally {
        if (mount) {
          setLoadingCourses(false);
        }
      }
    };

    if (Number(clientId) <= 0) {
      navigate(getRouteFrom(REDIRECT));
    } else {
      void syncCourses();
    }

    return () => {
      mount = false;
    };
  }, [clientId, navigate, lastClientId, isClientIdReady, search]);

  const searchCourses = useCallback(
    (searchByButton = false) => {
      if (!loadingCourses || searchByButton) {
        setLoadingCourses(true);
        const pageAux = searchByButton
          ? 0
          : Number((paginationMeta?.page ?? 0) > 0 ? paginationMeta?.page : 0);
        getCoursesByClientId({
          clientId,
          page: pageAux + 1,
          limit: 15,
          filter: search,
        })
          .then((response: IPaginationCoursetDTO) => {
            setPaginationMeta(response.meta);

            if (!searchByButton) {
              setCourses(
                produce((draft) => {
                  draft.push(...response.data);
                })
              );
            } else {
              setCourses(response.data);
            }
          })
          .catch((error: IApiThrowsError) => {
            Logger.debug('error: ', error);
          })
          .finally(() => {
            setLoadingCourses(false);
          });
      }
    },
    [loadingCourses, paginationMeta, search, clientId]
  );

  const handleAcceptDelete = useCallback(async () => {
    if (courseToDelete && !loadingDelete) {
      setLoadingDelete(true);

      try {
        await deleteCourse({ id: courseToDelete.id }).then(() => {
          addToast({
            title: 'Course deleted',
            description: `Course ${truncateString(courseToDelete.title, TRUNCATE_MAX.TOAST)} was successfully deleted`,
            styleType: 'success',
            dataCy: 'success-toast',
          });

          searchCourses(true);
        });
      } catch (error) {
        addToast({
          title: 'Error on deleting course',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'course-deleted-error-toast',
        });
      } finally {
        setLoadingDelete(false);
        setCourseToDelete(null);
      }
    }
  }, [courseToDelete, loadingDelete, courses, addToast]);

  const handleRestoreCourse = useCallback(
    async (historyPage: ICourseVersion): Promise<void> => {
      if (loadingCourseRestore) {
        return;
      }

      setLoadingCourseRestore(true);
      try {
        await restoreCourseHistory({ courseVersion: historyPage.id });
        const courseRestore = await getCoursesById({
          id: historyPage.course.id,
        });

        const state: IEditCourseState = {
          course: courseRestore,
          from: generatePath(COURSES_PATH, { clientId }),
        };

        navigate(
          getRouteFrom(EDIT_COURSE)
            .replace(':clientId', clientId)
            .replace(':courseId', String(historyPage.course.id)),
          { state }
        );
      } catch (err) {
        addToast({
          title: 'Error on restore course',
          description: formatUnknownErrorMessage(
            err,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'course-restore-error-toast',
        });
      } finally {
        setLoadingCourseRestore(false);
        setCourseRestore(null);
      }
    },
    [loadingCourseRestore, clientId, navigate, addToast]
  );

  const statusCourseForm = useForm<StatusCourseSchema>({
    resolver: zodResolver(statusCourseSchema),
    defaultValues: {
      status: 'In Progress',
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const {
    handleSubmit: handleSubmitStatusCourse,
    setValue: setValueStatusCourse,
    reset: resetStatusCourse,
    formState: { errors: errorsStatusCourse, isValid: isValidStatusCourse },
    watch: watchStatusCourse,
  } = statusCourseForm;

  const valuesStatusCourse = watchStatusCourse();

  useEffect(() => {
    if (isModalChangeStatusOpen) {
      resetStatusCourse({
        status:
          (courseToChangeStatus?.status as
            | 'Completed'
            | 'Disabled'
            | 'In Progress') ?? 'In Progress',
      });
    }
  }, [courseToChangeStatus, isModalChangeStatusOpen, resetStatusCourse]);

  const handleAcceptChangeStatus: SubmitHandler<StatusCourseSchema> =
    useCallback(
      async (data: StatusCourseSchema) => {
        if (!courseToChangeStatus || loadingChangeStatus) {
          return;
        }

        setLoadingChangeStatus(true);
        try {
          const course: ICourseDTO = await updateCourse({
            ...courseToChangeStatus,
            status: data.status,
          });

          const modifiedCourseIndex = courses.findIndex(
            ({ id }) => id === course.id
          );
          if (modifiedCourseIndex >= 0) {
            const currentCourses = [...courses];
            currentCourses.splice(modifiedCourseIndex, 1);
            setCourses([course, ...currentCourses]);
          }
          addToast({
            title: 'Status updated',
            description: `Status of course ${truncateString(course.title, TRUNCATE_MAX.TOAST)} was successfully updated`,
            styleType: 'success',
            dataCy: 'success-toast',
          });
          resetStatusCourse({
            status: 'In Progress',
          });
          setCourseToChangeStatus(null);
        } catch (error) {
          addToast({
            title: 'Error on changing status',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
          });
          Logger.debug('error: ', error);
        } finally {
          setLoadingChangeStatus(false);
        }
      },
      [
        addToast,
        courseToChangeStatus,
        courses,
        loadingChangeStatus,
        resetStatusCourse,
      ]
    );

  const handleDeclineDelete = useCallback(() => {
    setCourseToDelete(null);
  }, []);

  const handleDeclineChangeStatus = useCallback(() => {
    if (!isSelectOpen.current.size) {
      resetStatusCourse({
        status: 'In Progress',
      });
      setCourseToChangeStatus(null);
    }
  }, [resetStatusCourse]);

  const courseDownload = (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => {
    e.stopPropagation();
    setCourseToDownload(course);
    setLoadingDownload(false);
  };

  const createViewURLForm = useForm<CreateViewURLSchema>({
    resolver: zodResolver(createViewURLSchema),
    defaultValues: {
      viewUrl: '',
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const {
    handleSubmit: handleSubmitCreateViewURL,
    setValue: setValueCreateViewURL,
    reset: resetCreateViewURL,
    formState: { errors: errorsCreateViewURL, isValid: isValidCreateViewURL },
    watch: watchCreateViewURL,
  } = createViewURLForm;

  const valuesCreateViewURL = watchCreateViewURL();

  useEffect(() => {
    if (isModalCreateViewURLOpen && courseToCreateViewURL) {
      resetCreateViewURL({
        viewUrl: courseToCreateViewURL?.viewURL ?? '',
      });
    }
  }, [isModalCreateViewURLOpen, courseToCreateViewURL, resetCreateViewURL]);

  const resetFormCreateViewURL = useCallback(() => {
    setIsModalCreateViewURLOpen(false);
    setCourseToCreateViewURL(null);
    resetCreateViewURL({
      viewUrl: '',
    });
  }, [resetCreateViewURL]);

  const viewCourse = (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => {
    e.stopPropagation();
    if (!course.viewURL || course.viewURL === '') {
      handleOpenModalCreateViewURL(e, course);
      return;
    }

    window.open(
      `${
        import.meta.env.VITE_COURSE_VIEW_SUBDOMAIN_URL ??
        window.location.origin + '/view'
      }/${course.viewURL}`,
      '_blank'
    );
  };

  const handleOpenModalCreateViewURL = useCallback(
    (e: MouseEvent<HTMLButtonElement | HTMLDivElement>, course: ICourseDTO) => {
      e.stopPropagation();
      if (!course.viewURL || course.viewURL === '') {
        setIsModalCreateViewURLOpen(true);
        setCourseToCreateViewURL(course);
        return;
      }

      if (!navigator.clipboard) {
        addToast({
          title: "Error on copy course's link",
          description:
            'Clipboard not available. Please try again or contact Benefit Education support.',
          styleType: 'error',
          dataCy: 'document-link-copied-error-toast',
        });
        return;
      }
      navigator.clipboard
        .writeText(
          `${
            import.meta.env.VITE_COURSE_VIEW_SUBDOMAIN_URL ??
            window.location.origin + '/view'
          }/${decodeURI(course.viewURL)}`
        )
        .then(() => {
          addToast({
            title: 'Link copied',
            description: `Link of course ${truncateString(course.title, TRUNCATE_MAX.TOAST)} was successfully copied`,
            styleType: 'success',
            dataCy: 'course-link-copied-success-toast',
          });
        })
        .catch((err) => {
          Logger.debug('err: ', err);

          addToast({
            title: "Error on copy course's link",
            description: formatUnknownErrorMessage(
              err,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'course-link-copied-error-toast',
          });
        });
    },
    [addToast]
  );

  const handleDeclineCreateViewURL = useCallback(() => {
    resetFormCreateViewURL();
  }, [resetFormCreateViewURL]);

  const handleOnChangeViewURL = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const sanitizedText = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
      if (sanitizedText.length <= 100) {
        setValueCreateViewURL('viewUrl', sanitizedText, {
          shouldValidate: true,
        });
      }
    },
    [setValueCreateViewURL]
  );

  const handleOpenModalDelete = (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => {
    e.stopPropagation();
    setCourseToDelete(course);
  };

  const handleOpenModalChangeStatus = useCallback(
    (e: MouseEvent<HTMLButtonElement | HTMLDivElement>, course: ICourseDTO) => {
      e.stopPropagation();
      setCourseToChangeStatus(course);
    },
    []
  );

  const generateCourseTemplateByCourseForm =
    useForm<GenerateCourseTemplateByCourseSchema>({
      resolver: zodResolver(generateCourseTemplateByCourseSchema),
      defaultValues: {
        courseId: 0,
        templateOptions: 0,
      },
      mode: 'onChange',
      criteriaMode: 'all',
    });

  const {
    handleSubmit: handleSubmitGenerateCourseTemplateByCourse,
    setValue: setValueGenerateCourseTemplateByCourse,
    reset: resetGenerateCourseTemplateByCourse,
    trigger: triggerGenerateCourseTemplateByCourse,
    formState: {
      errors: errorsGenerateCourseTemplateByCourse,
      isValid: isValidGenerateCourseTemplateByCourse,
    },
    watch: watchGenerateCourseTemplateByCourse,
  } = generateCourseTemplateByCourseForm;

  const valuesGenerateCourseTemplateByCourse =
    watchGenerateCourseTemplateByCourse();

  const handleOpenModalGenerateCourseTemplateByCourse = (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => {
    e.stopPropagation();
    setCourseGroupTemplate(course);
  };

  useEffect(() => {
    if (courseGroupTemplate) {
      resetGenerateCourseTemplateByCourse({
        courseId: courseGroupTemplate.id,
        templateOptions: 0,
      });
      void triggerGenerateCourseTemplateByCourse();
    }
  }, [
    courseGroupTemplate,
    resetGenerateCourseTemplateByCourse,
    triggerGenerateCourseTemplateByCourse,
  ]);

  const handleAcceptGenerateCourseTemplateByCourse: SubmitHandler<GenerateCourseTemplateByCourseSchema> =
    useCallback(
      async (data: GenerateCourseTemplateByCourseSchema) => {
        if (!courseGroupTemplate) {
          return;
        }

        try {
          setLoadingGenerateCourseTemplateByCourse(true);
          await createTemplateGroupByCourse({
            courseId: data.courseId,
            templateOptions: data.templateOptions ?? 0,
          });
          addToast({
            title: 'Course template generated',
            description: `Template of course ${truncateString(courseGroupTemplate.title, TRUNCATE_MAX.TOAST)} was successfully generated`,
            styleType: 'success',
            dataCy: 'course-template-generate-success-toast',
          });
          resetGenerateCourseTemplateByCourse({
            courseId: 0,
            templateOptions: 0,
          });
          setCourseGroupTemplate(null);
        } catch (error) {
          addToast({
            title: 'Error on generating course template',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
          });
          Logger.debug('error: ', error);
        } finally {
          setLoadingGenerateCourseTemplateByCourse(false);
        }
      },
      [courseGroupTemplate, addToast, resetGenerateCourseTemplateByCourse]
    );

  const declineGenerateCourseTemplateByCourse = useCallback(() => {
    if (!isSelectOpen.current.size) {
      resetGenerateCourseTemplateByCourse({
        courseId: 0,
        templateOptions: 0,
      });
      setCourseGroupTemplate(null);
    }
  }, [resetGenerateCourseTemplateByCourse]);

  const handleChangeTemplateOption = useCallback(
    (templateTypeStr: string) => {
      setValueGenerateCourseTemplateByCourse(
        'templateOptions',
        Number(templateTypeStr),
        {
          shouldValidate: true,
        }
      );
    },
    [setValueGenerateCourseTemplateByCourse]
  );

  const displayInMegaBytes = (value: number): number => {
    return Number((value / 1024 / 1024).toFixed(2));
  };

  const handleChangeImage = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      setChangeImage(null);
      setErrorMessageUpload('');
      const { files } = target;

      if (files?.item(0)) {
        const file = files.item(0);
        if (!file) return;
        const objectURL = URL.createObjectURL(file);
        const img = document.createElement('img');
        img.onload = () => {
          URL.revokeObjectURL(objectURL);

          if (
            img.width > HEADER_IMAGE_MAX_WIDTH ||
            img.height > HEADER_IMAGE_MAX_HEIGHT
          ) {
            setErrorMessageUpload(
              `The image dimensions must be less than ${HEADER_IMAGE_MAX_WIDTH} x ${HEADER_IMAGE_MAX_HEIGHT} px (width x height)`
            );
            return;
          }

          if (file.size > HEADER_IMAGE_MAX_SIZE) {
            setErrorMessageUpload(
              `The image size must be less than ${displayInMegaBytes(
                HEADER_IMAGE_MAX_SIZE
              )} MB`
            );
          }
        };

        img.src = objectURL;

        setChangeImage(file);
      }
    },
    []
  );

  const handleAcceptChangeImage = useCallback(() => {
    if (courseToChangeImage && !loadingChangeImage && changeImage) {
      setLoadingChangeImage(true);
      changeImageCourse({ id: courseToChangeImage.id, newImage: changeImage })
        .then((modifiedCourse) => {
          const modifiedCourseIndex = courses.findIndex(
            ({ id }) => id === modifiedCourse.id
          );
          if (modifiedCourseIndex >= 0) {
            setCourses(
              produce((draft) => {
                draft.splice(modifiedCourseIndex, 1);
                draft.unshift(modifiedCourse);
              })
            );
            addToast({
              title: 'Success on update image',
              description: `The preview course image has been changed`,
              styleType: 'success',
              dataCy: 'success-toast',
            });
          }
        })
        .catch((error) => {
          Logger.debug('error', error);
          addToast({
            title: 'Error on update image',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'update-image-error-toast',
          });
        })
        .finally(() => {
          setLoadingChangeImage(false);
          setCourseToChangeImage(null);
          setChangeImage(null);
        });
    }
  }, [courseToChangeImage, loadingChangeImage, changeImage, courses, addToast]);

  const handleDeclineChangeImage = useCallback(() => {
    setCourseToChangeImage(null);
    setChangeImage(null);
    setErrorMessageUpload('');
  }, []);

  const handleOpenModalChangeImage = (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => {
    e.stopPropagation();
    setCourseToChangeImage(course);
  };

  const handleOpenModalCourseRestore = (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => {
    e.stopPropagation();
    setCourseRestore(course);
  };

  const handleDeclineCourseRestore = useCallback(() => {
    setCourseRestore(null);
  }, []);

  const duplicateCourseForm = useForm<DuplicateCourseSchema>({
    resolver: zodResolver(duplicateCourseSchema),
    defaultValues: {
      clientId: Number(clientId),
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const {
    handleSubmit: handleSubmitDuplicate,
    setValue: setValueDuplicate,
    reset: resetDuplicate,
    formState: { errors: errorsDuplicate, isValid: isValidDuplicate },
  } = duplicateCourseForm;

  const handleOpenModalAutoBuild = () => {
    setIsModalAutoBuildOpen(true);
  };

  const handleCloseModalAutoBuild = () => {
    setIsModalAutoBuildOpen(false);
  };
  const handleGenerateAutobuild = useCallback(
    (data: IAutobuildGenerate) => {
      if (!loadingQuoteAction) {
        setLoadingQuoteAction(true);
        const { quoteId } = data;
        generateAutobuild(data, quoteId)
          .then(() => {
            addToast({
              title: 'Generated',
              description: `Course was generated.`,
              styleType: 'success',
              dataCy: 'autobuild-success-toast',
              duration: 3000,
            });
            // refresh courses
            searchCourses(true);
          })
          .catch((error: IApiThrowsError) => {
            addToast({
              title: 'Error on generating course',
              description: formatUnknownErrorMessage(error),
              styleType: 'error',
              dataCy: 'autobuild-error-toast',
              duration: 3000,
            });
          })
          .finally(() => {
            setIsModalAutoBuildOpen(false);
            setLoadingQuoteAction(false);
          });
      }
    },
    [addToast, loadingQuoteAction, searchCourses, setIsModalAutoBuildOpen]
  );

  useEffect(() => {
    if (dialogDuplicateOpen && courseDuplicate) {
      resetDuplicate({
        clientId: Number(clientId),
      });
    }
  }, [dialogDuplicateOpen, courseDuplicate, clientId, resetDuplicate]);

  const resetFormDuplicate = useCallback(() => {
    if (!isSelectOpen.current.size) {
      setDialogDuplicateOpen(false);
      setLoadingDuplicate(false);
      setCourseDuplicate(null);
      resetDuplicate({
        clientId: Number(clientId),
      });
    }
  }, [clientId, resetDuplicate]);

  const handleClientDuplicateSelectChange = useCallback(
    (value: string) => {
      setValueDuplicate('clientId', Number(value), { shouldValidate: true });
    },
    [setValueDuplicate]
  );

  const handleToAdminDuplicateCourse = useCallback(
    (e: MouseEvent<HTMLButtonElement | HTMLDivElement>, course: ICourseDTO) => {
      e.stopPropagation();
      setCourseDuplicate(course);
      setValueDuplicate('clientId', Number(clientId), { shouldValidate: true });
      setDialogDuplicateOpen(true);
    },
    [clientId, setValueDuplicate]
  );

  const addCourseFromTemplates = useCallback(
    async ({
      pageTemplateIds,
    }: Pick<ICreateCourseFromTemplatesProps, 'pageTemplateIds'>) => {
      try {
        const courseResult = await createCourseFromTemplates({
          clientId,
          title: `New course from ${pageTemplateIds.length} template${
            pageTemplateIds.length === 1 ? '' : 's'
          }`,
          passcodeProtected: false,
          pageTemplateIds,
        });

        const state: IEditCourseState = {
          course: courseResult,
          from: generatePath(COURSES_PATH, { clientId }),
        };
        navigate(
          generatePath(getRouteFrom(EDIT_COURSE), {
            clientId: String(clientId),
            courseId: String(courseResult.id),
          }),
          { state }
        );
      } catch (error) {
        Logger.debug('error: ', error);
        addToast({
          title: 'Error on create course',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'create-course-from-template-error-toast',
        });
      }
    },
    [addToast, clientId, navigate]
  );

  const addCourseFromCourseTemplates = useCallback(
    async ({
      templateGroupId,
      title,
    }: Pick<
      ICreateCourseUsingCourseGroupProps,
      'templateGroupId' | 'title'
    >) => {
      try {
        const courseResult = await createCourseUsingCourseGroup({
          clientId,
          title:
            title === ''
              ? `New course from ${templateGroupId} group template`
              : title,
          passcodeProtected: false,
          templateGroupId,
        });

        const state: IEditCourseState = {
          course: courseResult,
          from: generatePath(COURSES_PATH, { clientId }),
        };
        navigate(
          generatePath(getRouteFrom(EDIT_COURSE), {
            clientId: String(clientId),
            courseId: String(courseResult.id),
          }),
          { state }
        );
      } catch (error) {
        Logger.debug('error: ', error);
        addToast({
          title: 'Error on create course',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'create-course-from-template-error-toast',
        });
      }
    },
    [addToast, clientId, navigate]
  );

  const handleAddCourseFromTemplate = () => {
    setIsModalCreateCourseFromTemplateOpen(true);
  };

  const handleDeclineAddCourseFromTemplate = () => {
    setIsModalCreateCourseFromTemplateOpen(false);
  };

  const handleAddCourseFromCourseTemplate = () => {
    setIsModalCreateCourseFromCourseTemplateOpen(true);
  };
  const handleDeclineAddCourseFromCourseTemplate = () => {
    setIsModalCreateCourseFromCourseTemplateOpen(false);
  };

  const handleAddBlankCourse = useCallback(() => {
    createCourse({
      clientId,
      title: 'Untitled',
      passcodeProtected: false,
    })
      .then((course) => {
        const state: IEditCourseState = {
          course,
          from: generatePath(COURSES_PATH, { clientId }),
        };

        navigate(
          getRouteFrom(EDIT_COURSE)
            .replace(':clientId', clientId)
            .replace(':courseId', String(course.id)),
          { state }
        );
      })
      .catch((error) => {
        Logger.debug('error: ', error);
      });
  }, [clientId, navigate]);

  const hasDropdownOpen =
    isDropdownActionsOpen || isDropdownAddCourseOpen || isDropdownOpenGrid;

  const canEditCourse = useCan(RolesNotViewer);

  const { canEditCourseBoolean, editCourseReference } = useMemo(() => {
    const canEditCourseBoolean = canEditCourse ?? false;
    return {
      canEditCourseBoolean,
      editCourseReference: canEditCourseBoolean
        ? getRouteFrom(EDIT_COURSE).replace(':clientId', clientId.toString())
        : '.',
    };
  }, [canEditCourse, clientId]);

  const handleClickOnCard = useCallback(
    (editCoursePath: string, course: ICourseDTO) => {
      const state: IEditCourseState = {
        course,
        from: pathname,
      };
      navigate(editCoursePath, { state });
    },
    [pathname, navigate]
  );

  const handleAcceptCreateViewURL: SubmitHandler<CreateViewURLSchema> =
    useCallback(
      async (data: CreateViewURLSchema) => {
        if (!courseToCreateViewURL) {
          return;
        }

        setLoadingCreateViewURL(true);
        try {
          const course: ICourseDTO = await updateCourse({
            ...courseToCreateViewURL,
            viewURL: encodeURI(data.viewUrl.trim()),
          });
          searchCourses(true);
          addToast({
            title: 'Link updated',
            description: `Link of course ${truncateString(course.title, TRUNCATE_MAX.TOAST)} was successfully updated`,
            styleType: 'success',
            dataCy: 'success-toast',
          });
          resetFormCreateViewURL();
        } catch (error: unknown) {
          addToast({
            title: 'Error while updating link',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
            duration: 5000,
          });
          Logger.debug('error: ', error);
        } finally {
          setLoadingCreateViewURL(false);
        }
      },
      [courseToCreateViewURL, addToast, resetFormCreateViewURL, searchCourses]
    );

  const handleDuplicateCourseSubmit: SubmitHandler<DuplicateCourseSchema> =
    useCallback(
      async (data: DuplicateCourseSchema) => {
        if (!courseDuplicate) return;

        setLoadingDuplicate(true);
        try {
          await duplicateCourse({
            id: courseDuplicate.id,
            clientId: data.clientId,
          });

          if (
            data.clientId === selectedClient?.id ||
            data.clientId === Number(clientId)
          ) {
            searchCourses(true);
          }

          addToast({
            title: 'Course duplicated',
            description: `Course ${truncateString(courseDuplicate.title, TRUNCATE_MAX.TOAST)} was successfully duplicated`,
            styleType: 'success',
            dataCy: 'success-toast',
          });

          resetFormDuplicate();
        } catch (err: unknown) {
          addToast({
            title: 'Error on duplicate course',
            description: formatUnknownErrorMessage(
              err,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
          });
        } finally {
          setLoadingDuplicate(false);
        }
      },
      [
        courseDuplicate,
        selectedClient?.id,
        clientId,
        addToast,
        resetFormDuplicate,
        searchCourses,
      ]
    );

  const handleAdminSubmitClientDuplicate = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      void handleSubmitDuplicate(handleDuplicateCourseSubmit)(e);
    },
    [handleSubmitDuplicate, handleDuplicateCourseSubmit]
  );

  const handleDuplicateCourse = useCallback(
    (
      e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
      { id, title }: ICourseDTO
    ) => {
      e.stopPropagation();
      setCourseDuplicate({ id, title } as ICourseDTO);
      setValueDuplicate('clientId', Number(clientId), { shouldValidate: true });
      setDialogDuplicateOpen(true);
    },
    [clientId, setValueDuplicate]
  );

  useEffect(() => {
    if (clientId !== lastClientId.current) {
      setSearch('');
      lastSearch.current = '';
      lastClientId.current = clientId;
      setIsClientIdReady(true);
    }
  }, [clientId]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (search.trim() !== lastSearch.current) {
      const execSearch = () => {
        lastSearch.current = search.trim();
        setCourses([]);
        searchCourses(true);
      };

      if (search.trim() === '') {
        execSearch();
      } else {
        timer = setTimeout(execSearch, 2000);
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [search]);

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectOpen.current.add(key);
    } else {
      isSelectOpen.current.delete(key);
    }
  }, []);

  const downloadForm = useForm<DownloadCourseSchema>({
    resolver: zodResolver(downloadCourseSchema),
    defaultValues: {
      orientation: 'portrait',
      language: 'en',
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const {
    handleSubmit: handleSubmitDownload,
    setValue: setValueDownload,
    watch: watchDownload,
    reset: resetDownload,
    formState: { isValid: isValidDownload },
  } = downloadForm;

  const valuesDownload = watchDownload();

  useEffect(() => {
    if (isModalDownloadOpen) {
      resetDownload({
        orientation: 'portrait',
        language: 'en',
      });
    }
  }, [isModalDownloadOpen, resetDownload]);

  const handleChangeOrientation = useCallback(
    (value: string) => {
      setValueDownload('orientation', value as 'landscape' | 'portrait', {
        shouldValidate: true,
      });
    },
    [setValueDownload]
  );

  const handleChangeLanguage = useCallback(
    (value: string) => {
      setValueDownload('language', value, { shouldValidate: true });
    },
    [setValueDownload]
  );

  const handleSubmitDownloadPdf = useCallback(
    async (data: DownloadCourseSchema) => {
      if (!courseToDownload) return;

      setLoadingDownload(true);
      try {
        const courseDownloadName = courseToDownload.title;
        const courseDownloadLanguage =
          languageData.find(({ key }) => key === data.language)?.value ??
          'English';
        const toastTitle = courseDownloadName + ' - ' + courseDownloadLanguage;

        const course = await downloadCourse({
          id: courseToDownload.id,
          language: data.language !== 'en' ? data.language : '',
          orientation: data.orientation,
        });

        addToast({
          title: toastTitle,
          description: course.message,
          styleType: 'success',
          dataCy: 'success-toast',
        });

        resetDownload({
          orientation: 'portrait',
          language: 'en',
        });
        setCourseToDownload(null);
      } catch (error) {
        setTimeout(() => {
          addToast({
            title: 'Error while downloading course',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
          });
        }, 2000);
      } finally {
        setLoadingDownload(false);
      }
    },
    [addToast, courseToDownload, resetDownload]
  );

  const handleToggleBookmarkCourse = useCallback(
    async (courseId: number) => {
      try {
        const updatedCourse = await toggleBookmarkCourse({ courseId });
        setCourses(
          produce((draft) => {
            const idx = draft.findIndex((c) => c.id === courseId);
            if (idx !== -1) {
              draft[idx] = updatedCourse;
            }
          })
        );
        addToast({
          title: updatedCourse.bookmark
            ? 'Course bookmarked'
            : 'Course unbookmarked',
          description: updatedCourse.bookmark
            ? 'This course has been added to your bookmarks.'
            : 'This course has been removed from your bookmarks.',
          styleType: 'success',
          dataCy: 'success-toast',
        });
      } catch (error) {
        Logger.debug('toggleBookmarkCourse error: ', error);
        addToast({
          title: 'Error on toggle bookmark course',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'error-toast',
        });
      }
    },
    [addToast]
  );

  const getQuotesDates = useCallback(async () => {
    const clientIdNum = parseInt(clientId);
    if (!clientIdNum || clientIdNum <= 0) return;
    if (!HighUserRoles.includes(currentRole?.key as Roles)) return;

    try {
      const response = await getQuotesDatesByCompany(String(clientIdNum));
      setQuotesDatesSelected(
        response.length
          ? response.find(
              (quote: IQuoteDateDTO) =>
                quote.id === Math.max(...response.map((quote) => quote.id))
            )
          : undefined
      );
    } catch (error) {
      addToast({
        title: 'Error on get quotes dates',
        description: formatUnknownErrorMessage(
          error,
          DefaultDescriptionBenefitEducation
        ),
        styleType: 'error',
        dataCy: 'error-toast',
        duration: 3000,
      });
    }
  }, [clientId, addToast, currentRole]);

  useEffect(() => {
    void getQuotesDates();
  }, [getQuotesDates]);

  const renderCourseActions = (
    course: ICourseDTO,
    editCoursePath: string,
    view: 'desktop' | 'grid' | 'mobile'
  ) => {
    return (
      <CourseActionsComponent
        course={course}
        editCoursePath={editCoursePath}
        view={view}
        canEditCourseBoolean={canEditCourseBoolean}
        currentRole={currentRole}
        handleClickOnCard={handleClickOnCard}
        courseDownload={courseDownload}
        handleToAdminDuplicateCourse={handleToAdminDuplicateCourse}
        handleDuplicateCourse={handleDuplicateCourse}
        viewCourse={viewCourse}
        handleOpenModalCreateViewURL={handleOpenModalCreateViewURL}
        handleOpenModalDelete={handleOpenModalDelete}
        handleOpenModalChangeStatus={handleOpenModalChangeStatus}
        handleOpenModalChangeImage={handleOpenModalChangeImage}
        handleOpenModalCourseRestore={handleOpenModalCourseRestore}
        handleOpenModalGenerateCourseTemplateByCourse={
          handleOpenModalGenerateCourseTemplateByCourse
        }
        setIsDropdownActionsOpen={setIsDropdownActionsOpen}
        setIsDropdownGridOpen={setIsDropdownGridOpen}
        setVisibleWhenCourseOptionsOpen={setVisibleWhenCourseOptionsOpen}
        toggleBookmarkCourse={() => void handleToggleBookmarkCourse(course.id)}
      />
    );
  };

  return (
    <MainContainer data-cy="page-container">
      <PageHeader
        title="Courses"
        additionalActions={
          <Can roles={HighUserRoles}>
            <DropdownButtonContainer data-cy="add-button-container">
              <Can roles={HighUserRoles}>
                {quotesDatesSelected && !isHiddenAutoBuild && (
                  <Button
                    dataCy="button-autoBuild"
                    onClick={handleOpenModalAutoBuild}
                  >
                    <ButtonContent>
                      <p>Auto Build Course</p>
                    </ButtonContent>
                  </Button>
                )}
              </Can>
              <BaseDropdown
                onOpenChange={(open) => {
                  setIsDropdownAddCourseOpen(open);
                }}
                customTrigger={
                  <Button dataCy="button-addCourse" noPaddingRight>
                    <ButtonContent>
                      <p>Add Course</p>
                      <IconContainer>
                        <ArrowDown />
                      </IconContainer>
                    </ButtonContent>
                  </Button>
                }
              >
                <DropdownItem
                  data-cy="option-useTemplates"
                  onClick={handleAddCourseFromTemplate}
                  style={{ marginBottom: '0.25rem' }}
                >
                  <p>
                    <strong data-cy="text-useTemplates">
                      Create course from template
                    </strong>
                  </p>
                </DropdownItem>
                <DropdownItem
                  data-cy="option-courseTemplates"
                  onClick={handleAddCourseFromCourseTemplate}
                  style={{ marginBottom: '0.25rem' }}
                >
                  <p>
                    <strong data-cy="text-courseTemplates">
                      Create course from course template
                    </strong>
                  </p>
                </DropdownItem>
                <DropdownItem
                  data-cy="option-useScratch"
                  onClick={handleAddBlankCourse}
                >
                  <p>
                    <strong data-cy="text-useScratch">
                      Create course from scratch
                    </strong>
                  </p>
                </DropdownItem>
              </BaseDropdown>
            </DropdownButtonContainer>
          </Can>
        }
        dataCy="title-courses"
      />

      <GridListOptions>
        <Option
          color={optionStyle === 'list' ? '#00426b' : '#414042'}
          data-cy="option-courseList"
          onClick={() => {
            selectListOptionStyle({ optionStyle: 'list' });
            setOptionStyle('list');
          }}
        >
          <ListIcon />
        </Option>
        <Option
          color={optionStyle === 'grid' ? '#00426b' : '#414042'}
          data-cy="option-courseGrid"
          onClick={() => {
            selectListOptionStyle({ optionStyle: 'grid' });
            setOptionStyle('grid');
          }}
        >
          <GridIcon />
        </Option>
      </GridListOptions>
      <MainContainerCustom data-cy="courses-card-list">
        <SearchBar
          dataCy="input-searchCourse"
          search={search}
          onChange={(e) => {
            setSearch(e.target.value.slice(0, 90));
          }}
          onClearInput={() => {
            setSearch('');
          }}
          loading={loadingCourses}
        />

        <ContentPagination dataCy="content-courses">
          {loadingCourses && courses.length === 0 ? (
            <LoadingContainer data-cy="loading-courses-container">
              <Loading
                data-cy="loading-courses"
                src={LoadingSpinnerAnimated}
                isLoading={loadingCourses}
              />
            </LoadingContainer>
          ) : optionStyle === 'list' && courses.length > 0 ? (
            <InfiniteScroll
              height={200}
              style={{
                overflow: 'auto',
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
              }}
              dataLength={courses.length} // This is important field to render the next data
              next={searchCourses}
              hasMore={courses.length < (paginationMeta?.total ?? 0)}
              loader={
                <LoadingContainer data-cy="loading-courses-container">
                  <LoadingList />
                </LoadingContainer>
              }
              endMessage={
                <CenteredText
                  message="You have seen it all"
                  dataCy="pagination-endLimit"
                />
              }
            >
              {courses.map((course) => {
                const { id, title, updatedAt, courseFile } = course;
                const editCoursePath = editCourseReference.replace(
                  ':courseId',
                  id.toString()
                );

                return (
                  <CourseCard
                    data-cy={`courses-card-${id}-container`}
                    key={id}
                    disabled={!canEditCourseBoolean}
                    isDropdownOpen={visibleWhenCourseOptionsOpen === course.id}
                    onClick={() => {
                      handleClickOnCard(editCoursePath, course);
                    }}
                  >
                    <ImageContainer data-cy={'course-image-container'}>
                      <LazyImageCustom
                        src={courseFile?.path ?? ''}
                        alt="course-image"
                        dataCy="image-logoCourse"
                        useCrossOrigin={false}
                      />
                    </ImageContainer>
                    <TextContent data-cy="info-container">
                      <CourseNameWithTooltip
                        title={title}
                        courseId={id}
                        course={course}
                      />
                      <Date data-cy="course-last-update">
                        {format(parseJSON(updatedAt), 'LLLL u')}
                      </Date>
                    </TextContent>
                    <CourseActionsContainer data-cy="actions-container">
                      <OnDesktopView data-cy="desktop-view">
                        {renderCourseActions(course, editCoursePath, 'desktop')}
                      </OnDesktopView>
                      <OnMobileView data-cy="mobile-view">
                        {renderCourseActions(course, editCoursePath, 'mobile')}
                      </OnMobileView>
                    </CourseActionsContainer>
                  </CourseCard>
                );
              })}
            </InfiniteScroll>
          ) : optionStyle === 'grid' && courses.length > 0 ? (
            <InfiniteScroll
              height={200}
              style={{
                overflow: 'auto',
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
              }}
              dataLength={courses.length} // This is important field to render the next data
              next={searchCourses}
              hasMore={courses.length < (paginationMeta?.total ?? 0)}
              loader={
                <LoadingContainer data-cy="loading-courses-container">
                  <LoadingList />
                </LoadingContainer>
              }
              endMessage={
                <CenteredText
                  message="You have seen it all"
                  dataCy="pagination-endLimit"
                />
              }
            >
              <GridContent>
                {courses.map((course) => {
                  const { id, title, courseFile } = course;
                  const editCoursePath = editCourseReference.replace(
                    ':courseId',
                    id.toString()
                  );
                  return (
                    <GridItem key={course.id}>
                      <CourseCardGrid
                        data-cy={`courses-card-${id}-container`}
                        disabled={!canEditCourseBoolean}
                        onClick={() => {
                          handleClickOnCard(editCoursePath, course);
                        }}
                        isDropdownOpen={
                          visibleWhenCourseOptionsOpen === course.id
                        }
                      >
                        <ImageContainerGrid data-cy={'course-image-container'}>
                          <LazyImageGrid
                            src={courseFile?.path ?? ''}
                            alt="course-image"
                            dataCy="image-logoCourse"
                            useCrossOrigin={false}
                          />
                        </ImageContainerGrid>
                        <ContentCard>
                          {course.status === 'Completed' && <ConcludedIcon />}
                          {course.status === 'Disabled' && <DisabledIcon />}
                          <CourseNameWithTooltip
                            title={title}
                            courseId={id}
                            course={course}
                            dataCy="label-card"
                          />

                          {renderCourseActions(course, editCoursePath, 'grid')}
                        </ContentCard>
                      </CourseCardGrid>
                    </GridItem>
                  );
                })}
              </GridContent>
            </InfiniteScroll>
          ) : (
            <CenteredText
              message="No courses found"
              dataCy="no-courses-found"
            />
          )}
        </ContentPagination>

        <AddCourseWithTemplateModal
          clientId={clientIdNumber}
          onAccept={addCourseFromTemplates}
          onDecline={handleDeclineAddCourseFromTemplate}
          open={!hasDropdownOpen && isModalCreateCourseFromTemplateOpen}
        />
        <AddCourseWithCoursesTemplateModal
          clientId={clientIdNumber}
          onAccept={addCourseFromCourseTemplates}
          onDecline={handleDeclineAddCourseFromCourseTemplate}
          open={!hasDropdownOpen && isModalCreateCourseFromCourseTemplateOpen}
        />
        <FormModal
          acceptText="Confirm"
          dataCy="title-delete"
          declineText="Cancel"
          loading={loadingDelete}
          mainText={`Are you sure you want to delete ${
            courseToDelete?.title ?? 'this course'
          }?`}
          onAccept={handleAcceptDelete}
          onDecline={handleDeclineDelete}
          onOpenChange={handleDeclineDelete}
          open={!hasDropdownOpen && isModalDeleteOpen}
        />
        <FormBase.Provider {...createViewURLForm}>
          <FormModal
            acceptText="Save"
            biggerModal
            dataCy="title-sendCourse"
            declineText="Cancel"
            loading={loadingCreateViewURL}
            mainText={`Send Course`}
            onAccept={handleSubmitCreateViewURL(handleAcceptCreateViewURL)}
            onDecline={handleDeclineCreateViewURL}
            onOpenChange={handleDeclineCreateViewURL}
            open={!hasDropdownOpen && isModalCreateViewURLOpen}
            zIndex={11}
            disabled={!isValidCreateViewURL}
          >
            <ViewURLModalContentWrapper>
              <ModalText>
                <strong data-cy="description-createURL">
                  Please create the URL that this Course will be available
                  through
                </strong>
              </ModalText>
              <ViewURLInputContainer>
                <ModalText data-cy="text-urlCourse">
                  {import.meta.env.VITE_COURSE_VIEW_SUBDOMAIN_URL ??
                    window.location.origin + '/view'}
                  /
                </ModalText>
                <FormBase.InputContent
                  dataCy="label-viewURL"
                  filled={!!valuesCreateViewURL.viewUrl}
                  label=""
                  errorMessage={errorsCreateViewURL.viewUrl?.message}
                >
                  <FormBase.InputText
                    dataCy="input-viewURL"
                    placeholder="Enter View URL"
                    id="viewUrl"
                    name="viewUrl"
                    autoFocus
                    maxLength={100}
                    type="text"
                    onChange={handleOnChangeViewURL}
                  />
                </FormBase.InputContent>
              </ViewURLInputContainer>
              <ModalText data-cy="description-linkCourse">
                This is the link you will share so people can view your course.
              </ModalText>
            </ViewURLModalContentWrapper>
          </FormModal>
        </FormBase.Provider>
        <UploadModal
          dataCy="title-changeImage"
          accept="image/*"
          acceptText="Update"
          declineText="Cancel"
          loading={loadingChangeImage}
          mainText={`Are you sure you want to change image?`}
          onAccept={handleAcceptChangeImage}
          onChange={handleChangeImage}
          disabled={!changeImage || Boolean(errorMessageUpload)}
          onDecline={handleDeclineChangeImage}
          onOpenChange={handleDeclineChangeImage}
          open={!hasDropdownOpen && isModalChangeImageOpen}
          errorMessage={errorMessageUpload}
          fileSizeLimit={`${displayInMegaBytes(HEADER_IMAGE_MAX_SIZE)} MB`}
          fileDimensionsLimit={`${HEADER_IMAGE_MAX_WIDTH} x ${HEADER_IMAGE_MAX_HEIGHT} px`}
        />

        <RestorePageModal
          open={Boolean(courseRestore) && !hasDropdownOpen}
          onAccept={handleRestoreCourse}
          onDecline={handleDeclineCourseRestore}
          course={courseRestore}
          zIndex={10}
        />

        <FormModal
          acceptText="Confirm"
          declineText="Cancel"
          dataCy="title-download"
          mainText="Download Course PDF"
          open={!hasDropdownOpen && isModalDownloadOpen}
          onDecline={resetFormDownload}
          onOpenChange={resetFormDownload}
          onAccept={handleSubmitDownload(handleSubmitDownloadPdf)}
          loading={loadingDownload}
          disabled={!isValidDownload}
        >
          <FormBase.Content>
            <FormBase.InputContent
              dataCy="label-orientation"
              filled={!!valuesDownload.orientation}
              label="Orientation"
              inputRef="orientation"
              value={valuesDownload.orientation}
              isSelectData
            >
              <SelectData
                data={[
                  { key: 'portrait', value: 'Portrait' },
                  { key: 'landscape', value: 'Landscape' },
                ]}
                dataCy="select-orientation"
                name="orientation"
                onValueChange={handleChangeOrientation}
                value={valuesDownload.orientation}
                zIndex={10}
                disabled={loadingDownload}
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'orientation');
                }}
                placeholder="Orientation"
              />
            </FormBase.InputContent>

            <FormBase.InputContent
              dataCy="label-language"
              filled={!!valuesDownload.language}
              label="Language"
              inputRef="language"
              value={valuesDownload.language}
              isSelectData
            >
              <SelectData
                data={languageData}
                dataCy="select-language"
                name="language"
                onValueChange={handleChangeLanguage}
                value={valuesDownload.language}
                zIndex={10}
                disabled={loadingDownload}
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'language');
                }}
                placeholder="Language"
              />
            </FormBase.InputContent>
          </FormBase.Content>
        </FormModal>

        <DuplicateCourseModal
          disabled={!isValidDuplicate}
          loading={loadingDuplicate}
          onAccept={handleAdminSubmitClientDuplicate}
          onDecline={resetFormDuplicate}
          onValueChange={handleClientDuplicateSelectChange}
          open={!hasDropdownOpen && dialogDuplicateOpen}
          errorMessage={errorsDuplicate.clientId?.message}
        />

        <FormBase.Provider {...statusCourseForm}>
          <FormModal
            acceptText="Update Status"
            dataCy="title-status"
            declineText="Cancel"
            loading={loadingChangeStatus}
            mainText={`Do you want to change the status of ${
              courseToChangeStatus?.title ?? 'this course'
            }?`}
            onAccept={handleSubmitStatusCourse(handleAcceptChangeStatus)}
            onDecline={handleDeclineChangeStatus}
            onOpenChange={handleDeclineChangeStatus}
            open={!hasDropdownOpen && isModalChangeStatusOpen}
            disabled={!isValidStatusCourse}
          >
            <FormBase.InputContent
              dataCy="label-status"
              filled={!!valuesStatusCourse.status}
              inputRef="status"
              label="Status"
              value={valuesStatusCourse.status}
              errorMessage={errorsStatusCourse.status?.message}
              isSelectData
            >
              <SelectData
                data={[
                  { key: 'Completed', value: 'Completed' },
                  { key: 'In Progress', value: 'In Progress' },
                  { key: 'Disabled', value: 'Disabled' },
                ]}
                dataCy="select-status"
                name="status"
                placeholder="Select Status"
                value={valuesStatusCourse.status}
                onValueChange={(value) => {
                  setValueStatusCourse(
                    'status',
                    value as 'Completed' | 'Disabled' | 'In Progress',
                    {
                      shouldValidate: true,
                    }
                  );
                }}
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'status');
                }}
              />
            </FormBase.InputContent>
          </FormModal>
        </FormBase.Provider>

        <FormBase.Provider {...generateCourseTemplateByCourseForm}>
          <FormModal
            acceptText="Confirm"
            dataCy="generate-course-template-by-course"
            declineText="Cancel"
            loading={loadingGenerateCourseTemplateByCourse}
            mainText="Do you want to generate a course template ?"
            onAccept={handleSubmitGenerateCourseTemplateByCourse(
              handleAcceptGenerateCourseTemplateByCourse
            )}
            onDecline={declineGenerateCourseTemplateByCourse}
            onOpenChange={declineGenerateCourseTemplateByCourse}
            open={!hasDropdownOpen && Boolean(courseGroupTemplate)}
            disabled={!isValidGenerateCourseTemplateByCourse}
          >
            <SelectContainer data-cy="template-type">
              <FormBase.InputContent
                dataCy="label-courseTemplateType"
                filled={!!valuesGenerateCourseTemplateByCourse.templateOptions}
                inputRef="templateOptions"
                label="Course Template Type"
                isSelectData
                errorMessage={
                  errorsGenerateCourseTemplateByCourse.templateOptions?.message
                }
              >
                <SelectData
                  data={templateTypesOptions}
                  dataCy="select-templateType"
                  name="templateOptions"
                  placeholder="Select type"
                  defaultValue={String(
                    valuesGenerateCourseTemplateByCourse.templateOptions
                  )}
                  onValueChange={handleChangeTemplateOption}
                  onOpenChange={(isOpen) => {
                    handleOpenChange(isOpen, 'course-template');
                  }}
                />
              </FormBase.InputContent>
            </SelectContainer>
          </FormModal>
        </FormBase.Provider>

        {quotesDatesSelected && !hasDropdownOpen && (
          <AutoBuildModal
            quotesDatesSelected={quotesDatesSelected}
            open={isModalAutoBuildOpen}
            zIndex={10}
            onDecline={handleCloseModalAutoBuild}
            onAccept={handleGenerateAutobuild}
            isActionRunning={loadingQuoteAction}
          />
        )}
      </MainContainerCustom>
    </MainContainer>
  );
};
